import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  checkPasswordStrength,
  isPasswordValid,
} from 'components/InputField/utils';
import { useTranslation } from 'react-i18next';
import InputField, {
  InputFieldProps,
} from 'components/InputField/InputField.component';
import { useField } from 'react-final-form';
import { ReactComponent as Eye } from 'icons/eye.svg';
import { ReactComponent as EyeCrossed } from 'icons/eye-crossed.svg';

import './PasswordField.styles.scss';

type PasswordFieldProps = {
  className?: string;
  withoutStrengthIndicator?: boolean;
} & InputFieldProps;

const PasswordField: React.FC<PasswordFieldProps> = (props) => {
  const { withoutStrengthIndicator, input, className, ...inputRest } = props;

  const [type, setType] = useState<'text' | 'password'>('password');

  const classes = classNames('password-field', className);

  const { t } = useTranslation();

  const passwordStrength = useMemo(() => {
    if (!input.value) return null;

    return checkPasswordStrength(input.value);
  }, [input.value]);

  const passwordDescription = useMemo(() => {
    if (withoutStrengthIndicator) return null;

    return input.value ? (
      <>
        <span>{t('passwordStrength')}</span>{' '}
        <span className={`${passwordStrength}`}>{t(passwordStrength)}</span>
      </>
    ) : (
      t('passwordInputDescription')
    );
  }, [withoutStrengthIndicator, input.value, passwordStrength, t]);

  const validate = useCallback(
    (newPassword) => {
      if (
        !newPassword ||
        withoutStrengthIndicator ||
        isPasswordValid(newPassword)
      )
        return null;

      return t('tooWeakPassword');
    },
    [withoutStrengthIndicator, t],
  );

  useField(input.name, {
    validate,
  });

  return (
    <InputField
      input={input}
      className={classes}
      description={passwordDescription}
      {...inputRest}
      type={type}
      suffixNode={
        <button
          type="button"
          aria-label="Show password."
          onClick={() =>
            setType((old) => (old === 'text' ? 'password' : 'text'))
          }
        >
          {type === 'text' ? <Eye /> : <EyeCrossed />}
        </button>
      }
    />
  );
};

export default PasswordField;
